import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GraduationCap, Loader2, Target, TrendingUp, Brain } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email.trim(), password.trim());
    setBusy(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-20 w-80 h-80 bg-emerald-300/20 rounded-full blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
            <GraduationCap className="text-white" size={26} />
          </div>
          <span className="font-display font-bold text-xl text-white">TYT/AYT Takip</span>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
            Hedefe giden yolu <span className="text-emerald-200">veriyle</span> yönet.
          </h1>
          <p className="text-white/80 text-base max-w-md">
            Yönetici, koç, öğrenci ve veli panelleriyle haftalık program, soru takibi, PPD analizi, deneme
            karşılaştırması ve hedef net sistemi tek çatı altında.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            {[
              { icon: Target, t: "Hedef Net" },
              { icon: TrendingUp, t: "Deneme Analizi" },
              { icon: Brain, t: "Zayıf Konular" },
            ].map((f, i) => (
              <div key={i} className="rounded-xl p-4 bg-white/10 border border-white/20 backdrop-blur-sm">
                <f.icon className="text-white mb-2" size={22} />
                <div className="text-sm text-white font-medium">{f.t}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-white/60 text-xs">© 2026 TYT/AYT Takip Sistemi</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <GraduationCap className="text-white" size={22} />
            </div>
            <span className="font-display font-bold text-lg text-slate-900">TYT/AYT Takip</span>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Giriş Yap</h2>
            <p className="text-slate-500 text-sm mt-1">Panelinize erişmek için giriş yapın.</p>
          </div>

          {error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label className="ta-label">E-posta veya Kullanıcı Adı</label>
            <input
              className="ta-input"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@mail.com veya ogr1234"
              required
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              spellCheck={false}
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="ta-label">Şifre</label>
            <input
              className="ta-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="current-password"
              spellCheck={false}
              data-testid="login-password"
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="login-submit">
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Giriş Yap"}
          </Button>
        </form>
      </div>
    </div>
  );
}
